import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import NotificationService from './notificationService';
import { getPortalMessagesForMember } from './portalMessagesService';
import RosterTeamChatService from './rosterTeamChatService';
import GroupChatService from './groupChatService';

/**
 * Unread count for YAU inbox (admin posts + portal messages), same rules as Messages page.
 */
export async function fetchYauInboxUnreadCount(userId) {
  if (!userId) return 0;
  try {
    const adminRes = await NotificationService.getAdminNotifications();
    const raw = (adminRes.data || []).filter((n) => n.deliveryMode !== "fanout");
    const adminFormatted = raw.map((notif) => ({
      id: notif.id,
      read: notif.read === true,
    }));
    let portalFormatted = [];
    try {
      portalFormatted = await getPortalMessagesForMember(userId);
    } catch {
      portalFormatted = [];
    }
    const portalRows = portalFormatted.map((m) => ({ id: m.id, read: m.read === true }));
    const combined = [...portalRows, ...adminFormatted];
    return combined.filter((m) => !m.read).length;
  } catch (e) {
    console.warn('fetchYauInboxUnreadCount:', e);
    return 0;
  }
}

async function resolveGroupChats(user) {
  let groupChats = [];
  try {
    groupChats = await RosterTeamChatService.getUserTeamChats(user);
    if (!groupChats.length) throw new Error('no api chats');
  } catch {
    try {
      const firebaseChats = await GroupChatService.getUserGroupChats(user.email, user.uid);
      groupChats = firebaseChats.map((chat) => ({
        ...chat,
        userRole: 'member',
        rosterId: chat.id,
      }));
    } catch {
      groupChats = [];
    }
  }
  return groupChats;
}

/**
 * Unread team chat messages (same idea as Messages.jsx listeners, one-shot for nav badge).
 */
export async function fetchTeamChatsUnreadTotal(user) {
  if (!user?.uid) return 0;
  try {
    const groupChats = await resolveGroupChats(user);
    if (!groupChats.length) return 0;

    const memberSnap = await getDoc(doc(db, 'members', user.uid));
    const lastReadTimestamps = memberSnap.exists()
      ? memberSnap.data().lastReadTimestamps || {}
      : {};

    let total = 0;
    for (const chat of groupChats) {
      const messages = await GroupChatService.getChatMessages(chat.id, 50, false);
      const lr = lastReadTimestamps[chat.id]?.seconds
        ? new Date(lastReadTimestamps[chat.id].seconds * 1000)
        : new Date(0);
      for (const msg of messages) {
        const ts = msg.timestamp?.toDate
          ? msg.timestamp.toDate()
          : msg.timestamp
            ? new Date(msg.timestamp)
            : new Date(0);
        if (ts > lr && msg.uid !== user.uid) {
          total += 1;
        }
      }
    }
    return total;
  } catch (e) {
    console.warn('fetchTeamChatsUnreadTotal:', e);
    return 0;
  }
}

export async function fetchTotalMessagesUnreadCount(user) {
  if (!user?.uid) return 0;
  const [inbox, team] = await Promise.all([
    fetchYauInboxUnreadCount(user.uid),
    fetchTeamChatsUnreadTotal(user),
  ]);
  return inbox + team;
}
